import { dehydrate, hydrate } from '@tanstack/react-query'
import type { DehydratedState, QueryClient } from '@tanstack/react-query'

import { VORRAT, lauf } from './idb'

/**
 * Was die App zuletzt vom Server wusste -- damit ohne Netz etwas dasteht (#13).
 *
 * **Warum es das braucht.** Der Zwischenspeicher von react-query liegt im
 * Arbeitsspeicher und ist nach dem Schließen weg. Ohne Netz stand die App
 * danach nicht nur ohne Beiträge da, sie kam gar nicht erst herein: `useMe()`
 * scheiterte, und `RequireAuth` schickte weiter zur Anmeldung. Ein Autor
 * im Funkloch soll den Bericht von gestern lesen können.
 *
 * **Vorgehalten wird, was ohnehin geholt wurde**, nichts darüber hinaus. Beim
 * Start eine Runde durch alle Reisen zu drehen, damit sie später offline
 * dastehen, kostete bei jedem Öffnen Datenverkehr -- und unterwegs ist das
 * Mobilfunk.
 *
 * **Von Hand und ohne zusätzliches Paket.** `dehydrate`/`hydrate` liegen
 * ohnehin in `@tanstack/react-query`, und eine IndexedDB gibt es für den
 * Ausgangskorb schon. Der eigentliche Grund gegen einen fertigen Persister ist
 * aber ein anderer: der Vorrat muss **vor dem ersten Zeichnen** dastehen. Käme
 * er später, hätte `RequireAuth` längst zur Anmeldung weitergeleitet, und die
 * gerettete Chatliste träfe auf eine Seite, die es nicht mehr gibt.
 */

/** Ein fester Schlüssel: hier steht immer genau ein Satz. */
const SCHLUESSEL = 'stand'

/**
 * Wie lange ein gespeicherter Stand gilt.
 *
 * Sieben Tage, und die Grenze ist eine fachliche: nach einer Woche ohne Netz
 * ist ein Reisebericht keine Auskunft mehr, sondern eine Erinnerung, die sich
 * für Auskunft ausgibt. Dann lieber leer und ehrlich.
 */
const HALTBAR_MS = 7 * 24 * 60 * 60 * 1000

/**
 * Wie lange auf die Datenbank gewartet wird, bevor ohne Vorrat gezeichnet wird.
 *
 * Der Vorrat sitzt vor dem ersten Bild; eine hängende Datenbank (ein zweites
 * Fenster hält die alte Version, der Speicher ist gesperrt) dürfte die
 * Anwendung nicht anhalten. Nichts daran ist fatal -- im schlimmsten Fall
 * fehlen die alten Beiträge.
 */
const GEDULD_MS = 1500

/** Ein Schreibvorgang je Sekunde reicht; getippt wird hier nicht. */
const DROSSEL_MS = 1000

interface Satz {
  /** Der Stand der Oberfläche, der ihn geschrieben hat. */
  build: string
  /** Wann, in Millisekunden seit 1970. */
  gespeichert: number
  stand: DehydratedState
}

/**
 * Welche Abfragen auf die Platte dürfen -- eine Positivliste, keine Ausnahmen.
 *
 * **Die Richtung ist der Punkt.** Wer morgen einen Schlüssel hinzufügt, muss
 * ihn hier ausdrücklich freigeben; bei einer Ausschlussliste schriebe er ihn
 * versehentlich mit. Zwei Einträge stünden auf einer solchen Liste besonders
 * schmerzhaft:
 *
 * - `health` -- die `StandWache` vergliche gegen einen alten Serverstand und
 *   meldete einen Fehlalarm („diese Seite ist älter als der Server"), obwohl
 *   beide gleich sind.
 * - `invitation-status` -- ein Einladungscode ist ein Schlüssel und hat auf
 *   der Platte nichts verloren.
 *
 * Die Verwaltungssachen (`speicher`, `people`, `invitations`, `requests`,
 * `medienpruefung`, `abgleich`) fehlen aus einem einfacheren Grund: ohne Netz
 * nützt keine davon etwas.
 *
 * **Drei kamen mit 1.34.0 dazu** (Codereview E8), und alle drei aus demselben
 * Grund: sie tragen etwas, das ohne Netz noch da sein soll.
 *
 * - `entwurf` -- der Reisebericht, den jemand unterwegs schreibt. Ausgerechnet
 *   der fehlte, und unterwegs ist genau die Lage ohne Netz.
 * - `tage` -- die Tagestrenner im Verlauf. Ohne sie steht der Chat ohne
 *   Gliederung da, obwohl die Beiträge selbst aus dem Vorrat kommen.
 *
 * **`members` stand hier und ist wieder gegangen, und der Grund gehört
 * aufgeschrieben.** Das Review nannte sie, weil der Einzelchat ohne sie kurz
 * seine Eingabezeile verliert -- eine Kleinigkeit. Der Preis war größer:
 * `einladungslink.mjs` wurde rot, und zwar mit „Empfänger (1)", nachdem gerade
 * zwei aufgenommen worden waren. Nach einem Neuladen stand die gespeicherte
 * Liste da, und die kannte den zweiten nicht.
 *
 * Das ist kein Fehler des Prüflaufs, sondern genau das, was ein Mensch
 * gesehen hätte: eine falsche Zahl an der Empfängerkarte. Eine Mitgliedschaft
 * ist eine **Berechtigung**; sie aus einem Vorrat zu zeigen heißt, eine
 * Auskunft über Rechte zu geben, die vielleicht nicht mehr gilt. Ein Verlauf
 * darf altern, eine Mitgliederliste nicht.
 *
 * `galerie`, `suche` und `stats` bleiben draußen: die erste ist eine
 * blätternde Liste, deren Seiten einzeln nichts taugen, die zweite eine Frage
 * an den Server, und die dritte eine Rechnung über alles, was es gibt.
 */
const ERLAUBT = new Set([
  'me',
  'threads',
  'trips',
  'trip',
  'messages',
  'highlights',
  'tracks',
  'reisefotos',
  'settings',
  'entwurf',
  'tage',
])

/**
 * Den gespeicherten Stand einlesen und in den Zwischenspeicher legen.
 *
 * Gibt zurück, ob etwas übernommen wurde -- der Aufrufer sagt es niemandem,
 * aber die Prüfläufe fragen danach.
 */
export async function vorratLaden(client: QueryClient): Promise<boolean> {
  try {
    const satz = await Promise.race([
      lauf<Satz | undefined>(VORRAT, 'readonly', (store) => store.get(SCHLUESSEL)),
      new Promise<undefined>((_, ab) => setTimeout(() => ab(new Error('zu langsam')), GEDULD_MS)),
    ])
    if (satz === undefined) return false

    // **Dieselbe Regel wie beim Zwischenspeicher der Hülle**: ein neuer Stand
    // fängt bei null an. Ein Satz aus einer älteren Version könnte Felder
    // tragen, die es nicht mehr gibt -- und die Anwendung glaubte ihm.
    if (satz.build !== __BUILD__) {
      await vorratLeeren()
      return false
    }

    if (Date.now() - satz.gespeichert > HALTBAR_MS) {
      await vorratLeeren()
      return false
    }

    hydrate(client, satz.stand)
    return true
  } catch {
    // Kein IndexedDB, gesperrter Speicher, ein zweites Fenster auf der alten
    // Version: dann eben ohne Vorrat. Die Anwendung läuft vollständig weiter.
    return false
  }
}

let geplant: ReturnType<typeof setTimeout> | null = null

/**
 * Auf Änderungen im Zwischenspeicher hören und den Vorrat nachziehen.
 *
 * Gibt die Abbestellung zurück.
 */
export function vorratMitschreiben(client: QueryClient): () => void {
  const ab = client.getQueryCache().subscribe(() => {
    if (geplant !== null) return
    geplant = setTimeout(() => {
      geplant = null
      void schreiben(client)
    }, DROSSEL_MS)
  })

  return () => {
    if (geplant !== null) {
      clearTimeout(geplant)
      geplant = null
    }
    ab()
  }
}

async function schreiben(client: QueryClient): Promise<void> {
  try {
    const stand = dehydrate(client, {
      // **Was Daten hat, nicht was gerade „erfolgreich" heißt.** Der
      // Unterschied ist der ganze Zweck dieser Ablage: schlägt eine Abfrage
      // ohne Netz fehl, setzt react-query ihren Zustand auf `error` -- die
      // zuletzt geholten Daten bleiben dabei stehen, aber `status` ist nicht
      // mehr `success`. Mit der naheliegenden Bedingung käme im Funkloch also
      // **nichts** mehr durch, der Vorrat würde als leer weggeworfen, und beim
      // übernächsten Start stünde die App leer da. Genau dann, wenn sie
      // gebraucht wird.
      shouldDehydrateQuery: (query) =>
        query.state.data !== undefined && ERLAUBT.has(String(query.queryKey[0])),
      // Laufende Mutationen gehören in den Ausgangskorb, nicht hierher -- der
      // kann sie auch senden.
      shouldDehydrateMutation: () => false,
    })

    // **Nichts zu merken heißt: wegwerfen, nicht leer hinschreiben.** Nach dem
    // Abmelden räumt `vorratLeeren()` weg -- und gleich darauf meldete sich der
    // geleerte Zwischenspeicher, und ein leerer Satz stand wieder da. Er trug
    // zwar nichts, aber „nichts in einer Schachtel" ist nicht dasselbe wie
    // „keine Schachtel", und der Prüflauf misst die Schachtel.
    if (stand.queries.length === 0) {
      await vorratLeeren()
      return
    }

    const satz: Satz = { build: __BUILD__, gespeichert: Date.now(), stand }
    await lauf(VORRAT, 'readwrite', (store) => store.put(satz, SCHLUESSEL))
  } catch {
    // Voller Speicher, gesperrte Datenbank: dann steht beim nächsten Start
    // eben der ältere Satz da. Kein Grund, die Anwendung zu behelligen.
  }
}

/**
 * Alles wegwerfen.
 *
 * **Gerufen wird das beim Abmelden und bei einem echten 401.** Beides heißt:
 * dieses Gerät darf diese Beiträge nicht mehr zeigen. Der Zwischenspeicher der
 * Hülle bleibt dabei stehen -- dort liegen JavaScript, CSS und eine leere
 * Seite, nichts Persönliches.
 */
export async function vorratLeeren(): Promise<void> {
  if (geplant !== null) {
    // Sonst schriebe der eben geplante Lauf gleich wieder hin, was hier
    // gerade weggeworfen wird.
    clearTimeout(geplant)
    geplant = null
  }

  try {
    await lauf(VORRAT, 'readwrite', (store) => store.delete(SCHLUESSEL) as unknown as IDBRequest<undefined>)
  } catch {
    // Nichts zu leeren ist auch geleert.
  }

  // The pictures the worker keeps for a start without a net (1.90.0) go with
  // the posts: whoever logs out leaves no photos on the device. The name is
  // `BILDSPEICHER` in `public/sw.js` -- a worker is not part of the bundle,
  // so the name stands twice, and both places say so.
  try {
    if (typeof caches !== 'undefined') await caches.delete('tb-bilder')
  } catch {
    // No store, nothing to empty.
  }
}
